/**
 * Общие куски экранов кошелька. Правила остатка и курса живут на сервере
 * (docs/04-finance.md), здесь только их показ: цифра всегда с пояснением,
 * откуда она.
 */
import { Money, Provenance } from '@/components/money';
import type { WalletSummary } from '@/api/hooks';
import { formatNumber, formatRelative } from '@/lib/format';

export const KIND_LABELS: Record<string, string> = {
  virtual_card: 'Виртуальная карта',
  crypto: 'Крипто-кошелёк',
  direct_card: 'Обычная карта',
  other: 'Другое',
};

/** Откуда запись: руками, из пересланного сообщения, из блокчейна. */
export function recordSourceLabel(source: string): string {
  switch (source) {
    case 'forward':
      return 'из сообщения';
    case 'chain':
      return 'из блокчейна';
    case 'telegram':
      return 'из Telegram';
    case 'wallet':
      return 'с кошелька';
    default:
      return 'вручную';
  }
}

/** Откуда курс себестоимости. */
export function rateSourceLabel(source: string): string {
  switch (source) {
    case 'p2p':
      return 'P2P Bybit';
    case 'cbr':
      return 'ЦБ, запасной';
    case 'manual':
      return 'курс вручную';
    default:
      return 'курс пополнений';
  }
}

/** Правило курса кошелька одной строкой. */
export function ratePolicyText(wallet: WalletSummary): string {
  if (wallet.ratePolicy === 'market') return 'Себестоимость оплат — по курсу дня';
  return wallet.averageRate === null
    ? 'Себестоимость оплат — по курсу пополнений, их пока нет: берётся курс дня'
    : `Себестоимость оплат — по курсу пополнений, сейчас ${formatNumber(wallet.averageRate, 2)} ₽ за 1 ${wallet.currency}`;
}

/** Остаток кошелька по его правилу: цифра и пояснение, или честное «не показываем». */
export function WalletBalance({
  wallet,
  size = 'lg',
}: {
  wallet: WalletSummary;
  size?: 'sm' | 'lg' | 'xl';
}) {
  if (wallet.balanceMinor === null) {
    return (
      <div>
        <span
          style={{
            font: `500 ${size === 'sm' ? 13 : 15}px var(--font-text)`,
            color: 'var(--text-muted)',
          }}
        >
          Остаток не показывается
        </span>
        {size === 'sm' ? null : <Provenance>{wallet.balanceNote}</Provenance>}
      </div>
    );
  }

  const negative = wallet.balanceMinor < 0;
  return (
    <div>
      <Money
        minor={wallet.balanceMinor}
        currency={wallet.currency}
        size={size}
        tone={negative ? 'danger' : 'normal'}
      />
      {size === 'sm' ? null : (
        <Provenance>
          {negative
            ? 'Остаток отрицательный: проверьте, все ли пополнения внесены'
            : wallet.balanceNote}
          {wallet.balanceMode === 'provider' && wallet.balanceAt && !negative
            ? `, обновлено ${formatRelative(wallet.balanceAt)}`
            : ''}
        </Provenance>
      )}
    </div>
  );
}
